<script setup lang="ts">
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { MediaImageFragment } from '~/api/graphql/generated'
import { ImageSizes } from './Image.vue'

interface ImageSliderProps {
  mainImage?: MediaImageFragment | null
  floorPlanImage?: MediaImageFragment | null
  imageType?: ImageSizes
}

const props = withDefaults(defineProps<ImageSliderProps>(), {
  mainImage: null,
  floorPlanImage: null,
  imageType: 'room-overview',
})

// Splide options
const options = {
  type: 'slide',
  perPage: 1,
  arrows: false,
  pagination: false,
  drag: false,
  keyboard: false,
  wheel: false,
  speed: 600,
  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
} as const

// Splide instance and state
const splideRef = ref()
const currentSlide = ref(0)
const hasFloorPlan = computed(() => !!props.floorPlanImage?.fieldMediaImage?.url)

// Toggle functions
const showMainImage = () => {
  if (splideRef.value) {
    splideRef.value.go(0)
    currentSlide.value = 0
  }
}

const showFloorPlan = () => {
  if (splideRef.value && hasFloorPlan.value) {
    splideRef.value.go(1)
    currentSlide.value = 1
  }
}

// Listen to slide changes
const onMoved = (_splide: unknown, newIndex: number) => {
  currentSlide.value = newIndex
}
</script>

<template>
  <div class="image-slider-container">
    <Splide ref="splideRef" :options="options" :has-track="false" aria-label="Room images" @splide:moved="onMoved">
      <SplideTrack class="splide-track">
        <SplideSlide v-if="props.mainImage" class="splide-slide">
          <Image
            v-animate="{ animation: 'vertical-reveal' }"
            class="slider-image"
            :type="imageType"
            :entity="props.mainImage"
          />
        </SplideSlide>
        <SplideSlide v-if="props.floorPlanImage" class="splide-slide">
          <Image
            v-animate="{ animation: 'vertical-reveal' }"
            class="slider-image"
            :type="imageType"
            :entity="props.floorPlanImage"
          />
        </SplideSlide>
      </SplideTrack>
    </Splide>

    <!-- Apple-style toggle -->
    <div v-if="hasFloorPlan" class="image-toggle">
      <div class="toggle-pill">
        <div class="toggle-slider" :class="{ 'slide-right': currentSlide === 1 }" />
        <button
          class="toggle-option"
          :class="{ selected: currentSlide === 0 }"
          type="button"
          :aria-label="'Show main image'"
          @click="showMainImage"
        >
          <Icon name="image-icon" />
        </button>
        <button
          class="toggle-option"
          :class="{ selected: currentSlide === 1 }"
          type="button"
          :aria-label="'Show floor plan'"
          @click="showFloorPlan"
        >
          <Icon name="floorplan" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.image-slider-container {
  position: relative;
  overflow: hidden;

  .splide {
    .splide-track {
      overflow: hidden;
    }

    .splide-slide {
      align-content: center;
      .slider-image {
        width: 100%;
        height: auto;
        display: block;

        :deep(img) {
          width: 100%;
          height: auto;
          object-fit: cover;
          // @include fluid(min-height, 280px, 400px);
        }
      }
    }
  }

  .image-toggle {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    @include breakpoint(tl) {
      bottom: 16px;
    }

    .toggle-pill {
      position: relative;
      display: flex;
      width: 120px;
      height: 44px;
      background: var(--c-white);
      border-radius: 100px;
      border: 2px solid var(--c-white);
      overflow: hidden;

      @include breakpoint(tl) {
        width: 146px;
        height: 52px;
        border-width: 4px;
      }

      .toggle-slider {
        position: absolute;
        top: 0;
        left: 0;
        width: 50%;
        height: 100%;
        background: var(--c-primary);
        border-radius: 100px;
        transition: transform 0.3s ease;
        z-index: 1;

        &.slide-right {
          transform: translateX(100%);
        }
      }

      .toggle-option {
        position: relative;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        color: var(--c-text);
        cursor: pointer;
        z-index: 2;
        transition: color 0.3s ease;

        &.selected {
          color: var(--c-white);
        }

        &:hover {
          opacity: 0.8;
        }

        :deep(svg) {
          width: 20px;
          height: 20px;

          @include breakpoint(tl) {
            width: 22px;
            height: 22px;
          }
        }
      }
    }
  }
}
</style>
